import React from "react";
import { Popover, Tooltip, Typography } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";

const { Paragraph, Text } = Typography;

/** A table column header with an info icon that explains the column in plain language. */
const HeaderHelp: React.FC<{ label: string; help: React.ReactNode }> = ({ label, help }) => (
  <span style={{ whiteSpace: "nowrap" }}>
    {label}{" "}
    <Tooltip title={help} overlayStyle={{ maxWidth: 360 }}>
      <InfoCircleOutlined style={{ color: "#1677ff", cursor: "help" }} />
    </Tooltip>
  </span>
);

export default HeaderHelp;

/** Plain-language meanings of the airing columns in the Scores drawer. */
export const AIRING_COLUMN_HELP = {
  slotVsAverage:
    "How strong this time slot (weekday and hour, on this platform and channel) is compared with an average slot. " +
    "+20% means the slot normally draws 20% more than average, -15% means less. Built from every series that aired there, " +
    "after allowing for how strong each series is, so it measures the hour, not the shows.",
  expectedRate:
    "Hours of viewing (HOV) per minute of airtime that an average episode of this series would earn in this slot. " +
    "It is proportional to the average audience watching at any moment. Think of it as what the slot and the series " +
    "alone would deliver, before this particular episode comes into it.",
  actualRate:
    "What this airing actually earned: its hours of viewing (minutes watched divided by 60) divided by its length " +
    "in minutes. Same unit as the expected rate, so the two compare directly.",
  airingVsSlot:
    "Actual rate against expected rate, as a percentage. +10% means this airing drew 10% more than an average episode " +
    "of the series would have in the same slot. The episode's score is the average of this column over its airings.",
  mergedPieces:
    "Amagi sometimes reports one interrupted playout as several rows a few seconds apart. This is how many such rows " +
    "were stitched into this airing. Blank means it arrived as a single row.",
};

/** Plain-language meanings of the columns in the main Scores table. */
export const SCORE_COLUMN_HELP = {
  score:
    "How much better or worse this episode does than an average episode of its series, after taking out the effect " +
    "of when and where it aired. +5% means it drew 5% more audience than an average episode would have earned in the " +
    "same slots. It is the average over the episode's airings, pulled toward 0 when there are only a few of them.",
  bucket:
    "A coarse grade from the score: low is the bottom third of all episodes, average the middle third, high the top " +
    "third. The data tells the two ends apart well and the middle poorly, so the bucket is the safer thing to act on " +
    "than the exact rank.",
  confidence:
    "Strong when the score rests on at least 6 airings, thin when it rests on fewer. A thin score can move a lot once " +
    "the next few airings arrive, so treat it as provisional.",
  source:
    "Own: computed from this feed's or platform's own airings. Borrowed: that platform's data was too noisy to score " +
    "on its own, so the row shows the pooled score from the reliable platforms on the same channel instead.",
};

/** An info icon that opens a longer explanation on click, for controls rather than column headers. */
export const HelpPopover: React.FC<{ title: string; content: React.ReactNode }> = ({ title, content }) => (
  <Popover trigger="click" placement="bottomLeft" overlayStyle={{ maxWidth: 560 }} title={title} content={content}>
    <InfoCircleOutlined style={{ color: "#1677ff", cursor: "pointer", fontSize: 16 }} />
  </Popover>
);

export const ScopeHelp: React.FC = () => (
  <HelpPopover
    title="How the three score levels relate"
    content={
      <>
        <Paragraph>
          Every airing is scored against the slot it landed in, on its own platform. From there the scores are
          built up in tiers, and each level is a view of the same episodes:
        </Paragraph>
        <Paragraph>
          <Text strong>Per platform.</Text> The episode&apos;s airings on one platform and channel, averaged. Shown as{" "}
          <Text italic>own</Text> when that platform&apos;s data is reliable enough to stand alone, or <Text italic>borrowed</Text>{" "}
          when it is too noisy, in which case the row carries the pooled score from the reliable platforms of the same channel.
        </Paragraph>
        <Paragraph>
          <Text strong>Per feed.</Text> One schedule goes out to many platforms at once (Nosey INTL 1 reaches about twenty),
          so the scheduler needs one number per feed. It is the reliable platforms&apos; scores blended together, each weighted
          by how many minutes that platform watched, so the big audiences count most.
        </Paragraph>
        <Paragraph style={{ marginBottom: 0 }}>
          <Text strong>Overall.</Text> The episode across every channel and platform, blended by how trustworthy each
          platform&apos;s score is (its airings times its reliability) rather than by audience size. This is the best single
          estimate of how good the episode is, and the one written onto the episode record.
        </Paragraph>
      </>
    }
  />
);

export const SeriesHelp: React.FC = () => (
  <HelpPopover
    title="Scores are relative to the series"
    content={
      <>
        <Paragraph>
          A score says how an episode does against an <Text strong>average episode of its own series</Text>, not against
          every episode on the channel. +5% on a Sally episode means 5% above an average Sally episode; in absolute
          audience that is still less than an average Jerry Springer episode, because the series itself draws less.
        </Paragraph>
        <Paragraph style={{ marginBottom: 0 }}>
          So comparing scores is most meaningful within one series, which is what this filter is for. The buckets
          (low, average, high) are cut across all series, which works because every series is centred on zero.
        </Paragraph>
      </>
    }
  />
);
